import { useCallback, useState } from "react";
import { message } from "antd";
import { getBlob, postWithHeaders } from "../utils/apiUtils";
import { extractFilename, triggerDownload } from "../utils/fileDownload";

type Method = "GET" | "POST";

export type ExportArgs = {
  endpoint: string;
  method?: Method;
  params?: Record<string, any>;
  data?: any;
  headers?: Record<string, string>;
  accept?: string;
  fallbackPrefix?: string; // p.sh. "highlights"
  ext?: string; // p.sh. "xlsx"
};

// 👉 Input për exportNow: endpoint opsional (se mund të vijë nga defaults)
type ExportArgsInput = Omit<ExportArgs, "endpoint"> & { endpoint?: string };

export function useExporter(defaults?: Partial<ExportArgs>) {
  const [exporting, setExporting] = useState(false);

  const exportNow = useCallback(
    async (args?: ExportArgsInput) => {
      const merged = { ...defaults, ...(args ?? {}) };

      const endpoint = merged.endpoint;
      const method = merged.method ?? "GET";
      const params = merged.params;
      const data = merged.data;
      const headers = merged.headers;
      const accept = merged.accept ?? "*/*";
      const fallbackPrefix = merged.fallbackPrefix ?? "export";
      const ext = merged.ext ?? "xlsx";

      if (!endpoint) {
        message.error("Export endpoint is missing");
        return;
      }

      const token = localStorage.getItem("token") || "";

      try {
        setExporting(true);

        if (method === "GET") {
          const resp = await getBlob<Blob>(endpoint, {
            params,
            responseType: "blob",
            headers: {
              Accept: accept,
              Authorization: token ? `Bearer ${token}` : "",
              ...headers,
            },
          });

          if (resp.type === "error") {
            console.error(resp.error);
            message.error("Export failed");
            return;
          }

          const filename = extractFilename(
            resp.headers?.["content-disposition"],
            fallbackPrefix,
            ext
          );
          triggerDownload(resp.data, filename);
          message.success("Exported successfully");
          return;
        }

        // POST
        const resp = await postWithHeaders(endpoint, data, {
          headers: {
            Accept: accept,
            Authorization: token ? `Bearer ${token}` : "",
            ...headers,
          },
          responseType: "blob",
        });

        if ((resp as any)?.type === "error") {
          console.error((resp as any).data);
          message.error("Export failed");
          return;
        }

        const filename = extractFilename(
          (resp as any).headers?.["content-disposition"],
          fallbackPrefix,
          ext
        );
        triggerDownload((resp as any).data, filename);
        message.success("Exported successfully");
      } catch (e) {
        console.error(e);
        message.error("Export failed. Please try again.");
      } finally {
        setExporting(false);
      }
    },
    [defaults]
  );

  return { exporting, exportNow };
}
